import { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  RefreshCw, BarChart2, AlertCircle, ChevronDown, Calendar, BookOpen,
  ChevronUp, Brain, Download, Filter, Eye, EyeOff,
} from 'lucide-react';
import { CollapsibleCard } from '../components/CollapsibleCard';
import { AllocationChart } from '../components/AllocationChart';
import type { AllocationRecord } from '../components/AllocationChart';
import { LogicTree } from '../components/LogicTree';
import type { LogicNode } from '../components/LogicTree';

const API_BASE = '/api';

interface Strategy {
  id: number;
  symphony_id: string;
  name: string;
  allocation_percent: number;
}

interface DiagnosticsStatus {
  regime_loaded: boolean;
  regime_coverage_pct: number;
  indicator_cache_hit: boolean;
  indicator_count: number;
  all_indicator_count: number;
}

interface DataResponse {
  data: AllocationRecord[];
  indicator_names?: string[];
  all_indicator_names?: string[];
  diagnostic_columns?: string[];
  diagnostics_status?: DiagnosticsStatus;
}

async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { credentials: 'include' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { detail?: string }).detail || `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

function SkeletonBlock({ className }: { className?: string }) {
  return <div className={`animate-pulse bg-gray-200 rounded ${className}`} />;
}

const today = new Date().toISOString().split('T')[0];
const yearAgo = (() => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 1);
  return d.toISOString().split('T')[0];
})();

// ---------------------------------------------------------------------------
// Regime label badge colors (Markov labels + Task #81 trend overlay labels)
// ---------------------------------------------------------------------------
const LABEL_COLORS: Record<string, string> = {
  // Markov labels
  TC_BULL_PERSISTENT:    'bg-emerald-100 text-emerald-800',
  BULL_TRANSITION:       'bg-green-100 text-green-700',
  MR_BALANCED_PERSISTENT:'bg-blue-100 text-blue-700',
  BEAR_TRANSITION:       'bg-orange-100 text-orange-700',
  TC_BEAR_PERSISTENT:    'bg-red-100 text-red-800',
  REGIME_SHIFT_RISK:     'bg-yellow-100 text-yellow-800',
  LOW_CONVICTION:        'bg-gray-100 text-gray-600',
  // Trend overlay labels (Task #81)
  BULLISH_TREND:         'bg-emerald-200 text-emerald-900',
  NEUTRAL_TREND:         'bg-slate-100 text-slate-600',
  BEARISH_TREND:         'bg-red-100 text-red-700',
  RISK_OFF:              'bg-red-200 text-red-900 font-bold',
  RECOVERY_ATTEMPT:      'bg-amber-100 text-amber-700',
};

function RegimeBadge({ label, muted }: { label?: string; muted?: boolean }) {
  if (!label) return <span className="text-gray-400 text-xs">—</span>;
  const cls = LABEL_COLORS[label] ?? 'bg-gray-100 text-gray-600';
  const short = label.replace(/_/g, ' ').replace('TC ', '').replace(' PERSISTENT', '');
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap ${muted ? 'opacity-60' : ''} ${cls}`}>
      {short}
    </span>
  );
}

// ---------------------------------------------------------------------------
// CSV export helper
// ---------------------------------------------------------------------------
function exportToCsv(records: AllocationRecord[], symbol: string) {
  if (records.length === 0) return;

  const hasRegime = records.some((r) => r.regime);
  const allIndKeys = Array.from(
    new Set(records.flatMap((r) => Object.keys(r.all_indicators ?? r.indicators ?? {})))
  ).sort();

  const headers = [
    'date', 'close_price', 'target_weight_pct', 'prev_weight_pct',
    'weight_change_pct', 'event_type',
    ...(hasRegime
      ? [
          'final_regime_label', 'trend_regime_label', 'markov_regime_label',
          'markov_score', 'bull_prob', 'bear_prob', 'neutral_prob',
          'transition_risk', 'confidence', 'persistence', 'weight_multiplier',
          'drawdown_from_63d_high', 'return_10d', 'return_20d',
          'close_vs_sma50_pct', 'sma20_vs_sma50_pct',
        ]
      : []),
    ...allIndKeys,
  ];

  const rows = records.map((r) => {
    const base = [
      r.date,
      r.close_price ?? '',
      (r.target_weight * 100).toFixed(4),
      (r.prev_weight * 100).toFixed(4),
      (r.weight_change * 100).toFixed(4),
      r.event_type ?? '',
    ];
    const regime = hasRegime ? [
      r.regime?.final_regime_label ?? '',
      r.regime?.trend_regime_label ?? '',
      r.regime?.regime_label ?? '',
      r.regime?.markov_score?.toFixed(4) ?? '',
      r.regime?.bull_probability?.toFixed(4) ?? '',
      r.regime?.bear_probability?.toFixed(4) ?? '',
      r.regime?.neutral_probability?.toFixed(4) ?? '',
      r.regime?.transition_risk?.toFixed(4) ?? '',
      r.regime?.confidence?.toFixed(4) ?? '',
      r.regime?.persistence?.toFixed(4) ?? '',
      r.regime?.weight_multiplier?.toFixed(4) ?? '',
      r.regime?.drawdown_from_63d_high?.toFixed(4) ?? '',
      r.regime?.return_10d?.toFixed(4) ?? '',
      r.regime?.return_20d?.toFixed(4) ?? '',
      r.regime?.close_vs_sma50_pct?.toFixed(4) ?? '',
      r.regime?.sma20_vs_sma50_pct?.toFixed(4) ?? '',
    ] : [];
    const indicators = allIndKeys.map((k) => {
      const src = r.all_indicators ?? r.indicators ?? {};
      return src[k] != null ? String(src[k]) : '';
    });
    return [...base, ...regime, ...indicators].join(',');
  });

  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `allocation_diagnostics_${symbol}_${today}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Decision Diagnostics Table
// ---------------------------------------------------------------------------
function DiagnosticsTable({
  data,
  symbol,
  diagnosticsStatus,
  allIndicatorNames,
}: {
  data: AllocationRecord[];
  symbol: string;
  diagnosticsStatus?: DiagnosticsStatus;
  allIndicatorNames: string[];
}) {
  const [showAllIndicators, setShowAllIndicators] = useState(false);
  const [filterSymbol, setFilterSymbol] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  const hasRegime = data.some((r) => r.regime);

  const visibleIndicators = useMemo(() => {
    if (!showAllIndicators) return [];
    if (filterSymbol) {
      return allIndicatorNames.filter((k) => k.toUpperCase().includes(symbol.toUpperCase()));
    }
    return allIndicatorNames;
  }, [showAllIndicators, filterSymbol, allIndicatorNames, symbol]);

  const pct = (v: number) => `${(v * 100).toFixed(1)}%`;
  const fmt2 = (v?: number) => v != null ? v.toFixed(2) : '—';
  const fmt3 = (v?: number) => v != null ? v.toFixed(3) : '—';
  const fmtPct2 = (v?: number) => v != null ? `${(v * 100).toFixed(1)}%` : '—';

  const EVENT_COLORS: Record<string, string> = {
    entry:    'bg-emerald-100 text-emerald-800',
    exit:     'bg-red-100 text-red-800',
    increase: 'bg-blue-100 text-blue-700',
    decrease: 'bg-amber-100 text-amber-700',
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Brain className="w-4 h-4 text-teal-600" />
          <span className="text-sm font-semibold text-gray-800">Decision Diagnostics</span>
          {diagnosticsStatus && (
            <span className="text-xs text-gray-400 font-normal">
              — {data.length} rows
              {hasRegime && (
                <> · regime {diagnosticsStatus.regime_coverage_pct.toFixed(0)}% covered</>
              )}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
          {/* Show/hide indicators toggle */}
          <button
            onClick={() => setShowAllIndicators((v) => !v)}
            className="flex items-center gap-1 px-2 py-1 text-xs rounded border border-gray-200 hover:bg-gray-100 text-gray-600 transition-colors"
            title={showAllIndicators ? 'Hide indicators' : 'Show indicator columns'}
          >
            {showAllIndicators ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            {showAllIndicators ? 'Hide Indicators' : 'Show Indicators'}
          </button>
          {showAllIndicators && (
            <button
              onClick={() => setFilterSymbol((v) => !v)}
              className={`flex items-center gap-1 px-2 py-1 text-xs rounded border transition-colors ${
                filterSymbol
                  ? 'bg-teal-50 border-teal-300 text-teal-700'
                  : 'border-gray-200 hover:bg-gray-100 text-gray-600'
              }`}
              title={filterSymbol ? 'Showing symbol-filtered indicators' : 'Showing all indicators'}
            >
              <Filter className="w-3 h-3" />
              {filterSymbol ? symbol : 'All'}
            </button>
          )}
          <button
            onClick={() => exportToCsv(data, symbol)}
            className="flex items-center gap-1 px-2 py-1 text-xs rounded border border-gray-200 hover:bg-gray-100 text-gray-600 transition-colors"
            title="Export to CSV"
          >
            <Download className="w-3 h-3" />
            CSV
          </button>
          {collapsed
            ? <ChevronDown className="w-4 h-4 text-gray-400" />
            : <ChevronUp className="w-4 h-4 text-gray-400" />
          }
        </div>
      </button>

      {!collapsed && (
        <div className="border-t border-gray-100">
          {/* Regime coverage warning */}
          {diagnosticsStatus && hasRegime && diagnosticsStatus.regime_coverage_pct < 50 && (
            <div className="flex items-start gap-2 px-5 py-2.5 bg-amber-50 border-b border-amber-100 text-xs text-amber-700">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              {/* Trade-era wording pointed at "Cache Admin", a page this product does not have
                  and never served — Lab owns its own research regime dataset and fills it with an
                  explicit, range-bounded backfill (RS-D4), not a nightly EOD job. There is no
                  Lab-native UI operation for it, and this hotfix does not add one; naming the
                  truth is better than naming a workflow that does not exist here. */}
              Regime research data covers only {diagnosticsStatus.regime_coverage_pct.toFixed(0)}% of this date range.
            </div>
          )}
          {!hasRegime && (
            <div className="flex items-start gap-2 px-5 py-2.5 bg-gray-50 border-b border-gray-100 text-xs text-gray-500">
              <Brain className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gray-400" />
              Regime research data is not populated for this symbol and date range.
            </div>
          )}

          <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="sticky top-0 bg-gray-50 border-b border-gray-200">
                  <th className="sticky left-0 z-10 bg-gray-50 px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap">Date</th>
                  <th className="px-3 py-2 text-right font-semibold text-gray-600 whitespace-nowrap">Weight</th>
                  <th className="px-3 py-2 text-right font-semibold text-gray-600 whitespace-nowrap">Price</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-600 whitespace-nowrap">Event</th>
                  {hasRegime && (
                    <>
                      <th className="px-3 py-2 text-center font-semibold text-teal-700 whitespace-nowrap">Final Regime</th>
                      <th className="px-3 py-2 text-center font-semibold text-gray-400 whitespace-nowrap">Markov</th>
                      <th className="px-3 py-2 text-right font-semibold text-teal-700 whitespace-nowrap">Score</th>
                      <th className="px-3 py-2 text-right font-semibold text-emerald-700 whitespace-nowrap">Bull%</th>
                      <th className="px-3 py-2 text-right font-semibold text-red-600 whitespace-nowrap">Bear%</th>
                      <th className="px-3 py-2 text-right font-semibold text-gray-600 whitespace-nowrap">Trans.Risk</th>
                      <th className="px-3 py-2 text-right font-semibold text-gray-600 whitespace-nowrap">Conf.</th>
                      <th className="px-3 py-2 text-right font-semibold text-orange-600 whitespace-nowrap">DD63d</th>
                      <th className="px-3 py-2 text-right font-semibold text-orange-600 whitespace-nowrap">Ret10d</th>
                      <th className="px-3 py-2 text-right font-semibold text-orange-600 whitespace-nowrap">Ret20d</th>
                    </>
                  )}
                  {visibleIndicators.map((k) => (
                    <th key={k} className="px-3 py-2 text-right font-semibold text-indigo-600 whitespace-nowrap max-w-[120px] truncate" title={k}>
                      {k.split('__')[0]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...data].reverse().map((r) => {
                  const isHighWeight = r.target_weight >= 0.15;
                  const isZero = r.target_weight < 0.005;
                  const rowBg = isHighWeight
                    ? 'bg-emerald-50/40 hover:bg-emerald-50/80'
                    : isZero
                    ? 'bg-gray-50/60 hover:bg-gray-100/60'
                    : 'hover:bg-gray-50';

                  const allSrc = r.all_indicators ?? r.indicators ?? {};

                  return (
                    <tr key={r.date} className={`border-b border-gray-100 transition-colors ${rowBg}`}>
                      <td className="sticky left-0 bg-inherit px-3 py-1.5 text-gray-700 font-mono whitespace-nowrap">
                        {r.date}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        <span className={isHighWeight ? 'font-semibold text-emerald-700' : isZero ? 'text-gray-400' : 'text-gray-700'}>
                          {pct(r.target_weight)}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-gray-600">
                        {r.close_price != null ? `$${r.close_price.toFixed(2)}` : '—'}
                      </td>
                      <td className="px-3 py-1.5 text-center">
                        {r.event_type ? (
                          <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${EVENT_COLORS[r.event_type] ?? 'bg-gray-100 text-gray-700'}`}>
                            {r.event_type}
                          </span>
                        ) : (
                          <span className="text-gray-300">·</span>
                        )}
                      </td>
                      {hasRegime && (
                        <>
                          <td className="px-3 py-1.5 text-center">
                            <RegimeBadge label={r.regime?.final_regime_label} />
                          </td>
                          <td className="px-3 py-1.5 text-center">
                            <RegimeBadge label={r.regime?.regime_label} muted />
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-teal-700">
                            {fmt2(r.regime?.markov_score)}
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-emerald-600">
                            {fmtPct2(r.regime?.bull_probability)}
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-red-600">
                            {fmtPct2(r.regime?.bear_probability)}
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-gray-600">
                            {fmt3(r.regime?.transition_risk)}
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-gray-600">
                            {fmt2(r.regime?.confidence)}
                          </td>
                          <td className={`px-3 py-1.5 text-right tabular-nums ${(r.regime?.drawdown_from_63d_high ?? 0) < -0.05 ? 'text-red-600 font-semibold' : 'text-orange-600'}`}>
                            {r.regime?.drawdown_from_63d_high != null ? `${(r.regime.drawdown_from_63d_high * 100).toFixed(1)}%` : '—'}
                          </td>
                          <td className={`px-3 py-1.5 text-right tabular-nums ${(r.regime?.return_10d ?? 0) < 0 ? 'text-red-500' : 'text-emerald-600'}`}>
                            {r.regime?.return_10d != null ? `${(r.regime.return_10d * 100).toFixed(1)}%` : '—'}
                          </td>
                          <td className={`px-3 py-1.5 text-right tabular-nums ${(r.regime?.return_20d ?? 0) < 0 ? 'text-red-500' : 'text-emerald-600'}`}>
                            {r.regime?.return_20d != null ? `${(r.regime.return_20d * 100).toFixed(1)}%` : '—'}
                          </td>
                        </>
                      )}
                      {visibleIndicators.map((k) => (
                        <td key={k} className="px-3 py-1.5 text-right tabular-nums text-indigo-700">
                          {allSrc[k] != null ? allSrc[k].toFixed(3) : '—'}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------
export function AllocationAnalysis() {
  const [selectedStrategyId, setSelectedStrategyId] = useState<number | null>(null);
  const [selectedSymbol, setSelectedSymbol] = useState<string>('');
  const [startDate, setStartDate] = useState(yearAgo);
  const [endDate, setEndDate] = useState(today);
  const [showDiagnostics, setShowDiagnostics] = useState(false);

  // ---- Strategies query ----
  const strategiesQuery = useQuery({
    queryKey: ['alloc-strategies'],
    queryFn: () => apiGet<{ strategies: Strategy[] }>('/allocation-analysis/strategies'),
    select: (res) => res.strategies,
  });

  const strategies = strategiesQuery.data ?? [];

  const resolvedStrategyId =
    selectedStrategyId ?? (strategies.length > 0 ? strategies[0].id : null);

  // ---- Symbols query ----
  const symbolsQuery = useQuery({
    queryKey: ['alloc-symbols', resolvedStrategyId],
    queryFn: () =>
      apiGet<{ symbols: string[] }>(`/allocation-analysis/${resolvedStrategyId}/symbols`),
    enabled: resolvedStrategyId !== null,
    select: (res) => res.symbols,
  });

  const symbols = symbolsQuery.data ?? [];
  const resolvedSymbol = selectedSymbol || (symbols.length > 0 ? symbols[0] : '');

  // ---- Chart data query ----
  const dataQueryKey = [
    'alloc-data',
    resolvedStrategyId,
    resolvedSymbol,
    startDate,
    endDate,
    showDiagnostics,
  ] as const;

  const dataQuery = useQuery({
    queryKey: dataQueryKey,
    queryFn: () => {
      const params = new URLSearchParams({
        symbol: resolvedSymbol,
        start_date: startDate,
        end_date: endDate,
        include_diagnostics: String(showDiagnostics),
      });
      return apiGet<DataResponse>(
        `/allocation-analysis/${resolvedStrategyId}/data?${params}`
      );
    },
    enabled: resolvedStrategyId !== null && resolvedSymbol !== '',
    staleTime: 60_000,
  });

  const chartData = dataQuery.data?.data ?? [];
  const indicatorNames = dataQuery.data?.indicator_names ?? [];
  const allIndicatorNames = dataQuery.data?.all_indicator_names ?? [];
  const diagnosticsStatus = dataQuery.data?.diagnostics_status;

  const strategyName = strategies.find((s) => s.id === resolvedStrategyId)?.name ?? '';

  // ---- Allocation logic query ----
  const logicQuery = useQuery({
    queryKey: ['alloc-logic', resolvedStrategyId, resolvedSymbol],
    queryFn: () =>
      apiGet<{ logic: LogicNode | null; symbol: string; reason?: string }>(
        `/allocation-analysis/${resolvedStrategyId}/logic?symbol=${encodeURIComponent(resolvedSymbol)}`
      ),
    enabled: resolvedStrategyId !== null && resolvedSymbol !== '',
    staleTime: 300_000,
  });

  const logicNode = logicQuery.data?.logic ?? null;
  const [logicExpanded, setLogicExpanded] = useState(true);

  const topError =
    strategiesQuery.error instanceof Error
      ? strategiesQuery.error.message
      : symbolsQuery.error instanceof Error
      ? symbolsQuery.error.message
      : dataQuery.error instanceof Error
      ? dataQuery.error.message
      : null;

  const handleStrategyChange = (id: number) => {
    setSelectedStrategyId(id);
    setSelectedSymbol('');
  };

  const handleToggleDiagnostics = useCallback(() => {
    setShowDiagnostics((v) => !v);
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart2 className="w-6 h-6 text-emerald-700" />
            Allocation Analysis
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Visualise price, weight history and allocation events for any symbol in a strategy.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Diagnostics toggle */}
          <button
            onClick={handleToggleDiagnostics}
            disabled={!resolvedSymbol}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
              showDiagnostics
                ? 'bg-teal-600 text-white hover:bg-teal-700'
                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
            title="Toggle Decision Diagnostics table"
          >
            <Brain className="w-4 h-4" />
            {showDiagnostics ? 'Diagnostics On' : 'Diagnostics'}
          </button>
          <button
            onClick={() => dataQuery.refetch()}
            disabled={dataQuery.isFetching || !resolvedSymbol}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-700 text-white rounded-lg text-sm font-medium hover:bg-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${dataQuery.isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error banner */}
      {topError && (
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{topError}</span>
        </div>
      )}

      {/* Controls */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Strategy selector */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
              Strategy
            </label>
            {strategiesQuery.isLoading ? (
              <SkeletonBlock className="h-10 w-full" />
            ) : strategies.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No strategies found</p>
            ) : (
              <div className="relative">
                <select
                  value={resolvedStrategyId ?? ''}
                  onChange={(e) => handleStrategyChange(Number(e.target.value))}
                  className="w-full appearance-none pl-3 pr-8 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  {strategies.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            )}
          </div>

          {/* Symbol selector */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
              Symbol
            </label>
            {symbolsQuery.isLoading ? (
              <SkeletonBlock className="h-10 w-full" />
            ) : symbols.length === 0 ? (
              <p className="text-sm text-gray-400 italic">
                {resolvedStrategyId !== null ? 'No cached symbols yet' : '—'}
              </p>
            ) : (
              <div className="relative">
                <select
                  value={resolvedSymbol}
                  onChange={(e) => setSelectedSymbol(e.target.value)}
                  className="w-full appearance-none pl-3 pr-8 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  {symbols.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            )}
          </div>

          {/* Start date */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
              Start Date
            </label>
            <div className="relative">
              <input
                type="date"
                value={startDate}
                max={endDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full pl-3 pr-9 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
              <Calendar className="absolute right-2.5 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* End date */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
              End Date
            </label>
            <div className="relative">
              <input
                type="date"
                value={endDate}
                min={startDate}
                max={today}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full pl-3 pr-9 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
              <Calendar className="absolute right-2.5 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>
      </div>

      {/* Allocation Logic card */}
      {resolvedSymbol && (logicNode || logicQuery.isFetching) && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <button
            onClick={() => setLogicExpanded((e) => !e)}
            className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-indigo-500" />
              <span className="text-sm font-semibold text-gray-800">
                Allocation Logic for{' '}
                <span className="text-indigo-700">{resolvedSymbol}</span>
              </span>
              <span className="text-xs text-gray-400 font-normal">
                — decision tree driven by this symbol
              </span>
            </div>
            {logicExpanded ? (
              <ChevronUp className="w-4 h-4 text-gray-400 shrink-0" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
            )}
          </button>

          {logicExpanded && (
            <div className="px-5 pb-5 pt-1 border-t border-gray-100">
              {logicQuery.isFetching ? (
                <div className="space-y-2 py-2">
                  <SkeletonBlock className="h-4 w-3/4" />
                  <SkeletonBlock className="h-4 w-1/2 ml-6" />
                  <SkeletonBlock className="h-4 w-2/3 ml-6" />
                </div>
              ) : logicNode ? (
                <div className="pt-2">
                  <LogicTree node={logicNode} symbol={resolvedSymbol} />
                </div>
              ) : (
                <p className="text-xs text-gray-400 italic py-2">
                  {logicQuery.data?.reason === 'no_dsl'
                    ? 'Logic view is only available for DSL-backed strategies.'
                    : 'Could not extract logic for this symbol.'}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Chart area */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
        {dataQuery.isFetching ? (
          <div className="space-y-3">
            <SkeletonBlock className="h-60 w-full" />
            <SkeletonBlock className="h-4 w-40" />
            <SkeletonBlock className="h-40 w-full" />
          </div>
        ) : !resolvedSymbol || strategies.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-gray-400 text-sm gap-2">
            <BarChart2 className="w-10 h-10 opacity-30" />
            <p>Select a strategy and symbol to view the chart.</p>
          </div>
        ) : chartData.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-gray-400 text-sm gap-2">
            <BarChart2 className="w-10 h-10 opacity-30" />
            <p>
              No weight history found for <strong>{resolvedSymbol}</strong> in this date range.
            </p>
            <p className="text-xs text-gray-400">
              Make sure the weight cache has been built for <strong>{strategyName}</strong>.
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <div>
                <span className="text-base font-semibold text-gray-900">{resolvedSymbol}</span>
                <span className="ml-2 text-sm text-gray-500">in {strategyName}</span>
              </div>
              <span className="text-xs text-gray-400">
                {chartData.length} trading days · {startDate} → {endDate}
              </span>
            </div>
            <AllocationChart
              data={chartData}
              symbol={resolvedSymbol}
              indicatorNames={indicatorNames}
            />
            <CollapsibleCard title="Summary" className="mt-6">
              <SummaryCards data={chartData} />
            </CollapsibleCard>
          </>
        )}
      </div>

      {/* Decision Diagnostics table — shown when diagnostics mode is on and we have data */}
      {showDiagnostics && chartData.length > 0 && !dataQuery.isFetching && (
        <DiagnosticsTable
          data={chartData}
          symbol={resolvedSymbol}
          diagnosticsStatus={diagnosticsStatus}
          allIndicatorNames={allIndicatorNames}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Summary cards (unchanged)
// ---------------------------------------------------------------------------
function SummaryCards({ data }: { data: AllocationRecord[] }) {
  const events = data.filter((r) => r.event_type !== null);
  const entries = events.filter((r) => r.event_type === 'entry').length;
  const exits = events.filter((r) => r.event_type === 'exit').length;
  const increases = events.filter((r) => r.event_type === 'increase').length;
  const decreases = events.filter((r) => r.event_type === 'decrease').length;

  const latestWeight = data.length > 0 ? (data[data.length - 1].target_weight * 100).toFixed(1) : '—';
  const maxWeight = data.length > 0
    ? (Math.max(...data.map((r) => r.target_weight)) * 100).toFixed(1)
    : '—';

  const cards = [
    { label: 'Latest Weight', value: `${latestWeight}%`, color: 'text-indigo-700' },
    { label: 'Peak Weight', value: `${maxWeight}%`, color: 'text-emerald-700' },
    { label: 'Entries', value: String(entries), color: 'text-emerald-600' },
    { label: 'Exits', value: String(exits), color: 'text-red-600' },
    { label: 'Increases', value: String(increases), color: 'text-blue-600' },
    { label: 'Decreases', value: String(decreases), color: 'text-amber-600' },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-6">
      {cards.map((c) => (
        <div key={c.label} className="bg-gray-50 rounded-lg p-3 text-center">
          <div className={`text-xl font-bold ${c.color}`}>{c.value}</div>
          <div className="text-xs text-gray-500 mt-0.5">{c.label}</div>
        </div>
      ))}
    </div>
  );
}
